import type { ApiRequestOptions } from '@hooks/useApi'
import useApi from '@hooks/useApi'
import type { NullableString } from '@shared_types/global_types'

export interface Language {
  code: string
  name: string
}

export type Translation = {
  key: string
  translation: string
}

export interface StoredTranslation {
  id?: number
  key: string
  code: string
  translation: string
}

const URL_ENABLED = 'api/i18n/languages/enabled'
const URL_TRANSLATIONS = 'api/i18n/translations'

export default function useI18nApi () {
  const { apiDelete, apiPost, apiGetArray } = useApi()

  const fetchTranslations = (code: string) => apiGetArray<Translation>(`api/i18n/translations/${code}`)

  const addTranslation = (t: StoredTranslation, options?: ApiRequestOptions) => {
    return apiPost<StoredTranslation>(URL_TRANSLATIONS, { translation: t.translation, code: t.code, key: t.key }, options)
  }

  const removeTranslation = (t: { key: string }, options?: ApiRequestOptions) => {
    return apiDelete(URL_TRANSLATIONS, t, options)
  }

  const fetchEnabledLanguages = () => apiGetArray<Language>(URL_ENABLED)

  const getEffectiveLanguageCode = (code: NullableString) =>
    fetchEnabledLanguages().then(languages => {
      const codes = languages.map(lng => lng.code)

      if (!code || !codes.includes(code)) {
        code = ''
      }

      if (!code && codes.includes('en')) {
        return { code: 'en', languages }
      }

      return { code: code || codes[0], languages }
    })

  return {
    upsertTranslation: addTranslation,
    removeTranslation,
    fetchAllLanguages: () => apiGetArray<Language>('api/i18n/languages/all'),
    fetchEnabledLanguages,
    saveEnabledLanguages: (codes: string[]): Promise<void> => apiPost(URL_ENABLED, { codes }),
    fetchTranslations,
    fetchAllTranslations: () => apiGetArray<StoredTranslation>('api/i18n/translations'),
    getEffectiveLanguageCode
  }
}
